import React, { useCallback, useEffect, useRef, useState } from 'react'
import style from './style.less'
import classNames from 'classnames'
import GridHeaderElement from './GridHeader'
import { isActiveRow, onMouseLeave, onMouseOver } from './helpers'
import GridCellElement from './GridCellElement'

interface GridProps {
  data: {
    rows: any[]
    options?: any
  }
  fetchData?: () => void
  headerSort: string[]
  onHeaderClicked?: ({
    index,
    sortOrder,
  }: {
    index: number
    sortOrder: any
  }) => void
  onRowClick?: (rowIndex: number) => void
  headers: string[]
}

interface GridPopoverElementProps {
  idx: number
  column: any
  data: any
}

const usePrevious = (value: any) => {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const onScroll = async (
  fetchData: () => void,
  loadingMore: boolean,
  setLoadingMore: (loadingMore: boolean) => void
) => {
  const grid = document.querySelector('#gridContainer')
  const rows = document.querySelectorAll('.gridItemSelector')
  const lastRow = rows[rows.length - 1]
  const top = lastRow?.getBoundingClientRect().top

  const CONTAINER_HEIGHT =
    (grid?.getBoundingClientRect()?.height || 0) +
    (grid?.getBoundingClientRect()?.top || 0)

  if (top && top <= CONTAINER_HEIGHT && !loadingMore) {
    setLoadingMore(true)
    await fetchData?.()
  }
}

const gridWithHeadersOnly = (dataRows: any) =>
  dataRows.map((col: any) => col.length === 1).indexOf(false) === -1

const GridPopoverElement = ({ idx, column, data }: GridPopoverElementProps) => {
  const isActive = isActiveRow(data, idx)
  return (
    <div
      className={classNames(
        style.gridItem,
        style.gridItemCenter,
        'gridItemSelector',
        `gridItem${idx}`,
        !isActive ? style.inactive : undefined
      )}
      onMouseOver={() => onMouseOver(idx)}
      onMouseLeave={() => onMouseLeave(idx)}
    >
      {column.popover}
      {column.text || column}
    </div>
  )
}

const Grid = ({
  data,
  headerSort,
  fetchData,
  onHeaderClicked,
  onRowClick,
  headers,
}: GridProps) => {
  const [dataRows, setDataRows] = useState<any[]>([])
  const [sortOrder, setSortOrder] = useState<(boolean | null)[]>([])
  const [loadingMore, setLoadingMore] = useState(false)
  const [top, setTop] = useState<number>()
  const gridEl = useRef(null)
  const prev = usePrevious(top)

  // This is done so the "Action" buttons like edit and delete, have a fixed position.
  // If the grid has no action, then fixedColumns must be set to 0, otherwise it will always
  // overflow horizontally, enabling the scroll bar.
  const fixedColumns = [...data.rows.keys()]
    .filter((i) => i >= data.rows.length - data.options?.fixedColumns)
    .reverse()

  useEffect(() => {
    const rows = document.querySelectorAll('.gridItemSelector')
    const lastRow = rows[rows.length - 1]
    const top = lastRow?.getBoundingClientRect().top
    setTop(top)
  }, [gridEl.current, dataRows[0]?.length])

  useEffect(() => {
    setDataRows(data.rows)
  }, [data.rows])

  useEffect(() => {
    const grid = document.querySelector('#gridContainer')
    const searchQuery = (
      document.querySelector('#searchInput') as HTMLInputElement
    )?.value
    const CONTAINER_HEIGHT =
      (grid?.getBoundingClientRect()?.height || 0) +
      (grid?.getBoundingClientRect()?.top || 0)
    const loadFullScreen = async () => {
      setLoadingMore(true)
      await fetchData?.()
      setLoadingMore(false)
    }

    if (top && top <= CONTAINER_HEIGHT && !searchQuery && !loadingMore) {
      if (prev && top !== prev) {
        loadFullScreen()
      } else if (!prev) {
        loadFullScreen()
      }
    }
  }, [prev, top, data, loadingMore, fetchData])

  useEffect(() => {
    setSortOrder(headers.map(() => null))
  }, [])

  useEffect(() => {
    if (fetchData) {
      const onScrollFun = () => onScroll(fetchData, loadingMore, setLoadingMore)
      window.addEventListener('scroll', onScrollFun, true)
      return () => window.removeEventListener('scroll', onScrollFun)
    }
  }, [data.rows?.length, fetchData, loadingMore])

  const headerClick = useCallback(
    (i: number) => {
      if (headerSort.indexOf(headers[i]) === -1) {
        return
      }

      const newSortOrder = headers.map((header, index) => {
        if (headerSort.indexOf(header) === -1) {
          return null
        }
        if (index === i) {
          const current = sortOrder[index]
          return current === null ? false : !current
        }
        return null
      })

      setSortOrder(newSortOrder)
      onHeaderClicked?.({
        index: i,
        sortOrder: newSortOrder,
      })
    },
    [sortOrder, headers, headerSort, onHeaderClicked]
  )

  return (
    <div
      className={classNames(
        style.gridContainer,
        gridWithHeadersOnly(dataRows) && style.empty
      )}
      id={'gridContainer'}
    >
      <div ref={gridEl} className={classNames(style.grid)}>
        {dataRows.map((columns, i) => (
          <div
            className={classNames(
              style.gridCol,
              fixedColumns?.indexOf(i) !== -1
                ? style.gridColFixedRight
                : undefined,
              style[`gridCOLUMNFixed${fixedColumns?.indexOf(i)}`],
              style[`gridCOLUMNSmall${data.options?.smallColumns?.indexOf(i)}`],
              i === dataRows.length - fixedColumns?.length
                ? style.gridCOLUMNShadow
                : undefined,
              data.options?.classes?.[`gridColFixedRight${i}`],
              data.options?.classes?.[`gridItem${i}`]
            )}
            key={i}
          >
            {columns.map((column: any, idx: number) => {
              return (
                <div
                  className={style.inline}
                  key={idx}
                  onClick={idx === 0 ? () => headerClick(i) : undefined}
                >
                  {idx === 0 ? (
                    <GridHeaderElement
                      key={i}
                      sortOrder={sortOrder[i]}
                      sortEnabled={
                        column &&
                        headerSort.indexOf(column.text || column) !== -1
                      }
                      column={column}
                      icon={columns[1]?.icon}
                    />
                  ) : column.popover ? (
                    <GridPopoverElement idx={idx} column={column} data={data} />
                  ) : (
                    <GridCellElement
                      i={i}
                      idx={idx}
                      column={column}
                      data={data}
                      onRowClick={onRowClick}
                    />
                  )}
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

export default Grid
